import { omitBy, mapValues, isPlainObject } from 'lodash-es';
import type {
  AnnotatedJsonSchema,
  EntityRootData,
  EntityOrConfiguration,
} from '@web-config-app/core';
import {
  getAllFeatureFlags,
  hasSchema,
  isConfigEntity,
} from '@web-config-app/core';
import {
  evaluateOmitIfCondition,
  getResolvedSchemaNodeAtPath,
} from '@web-config-app/schema-utils';

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value);

const getMergedCombinatorProperties = (
  schema: AnnotatedJsonSchema,
): AnnotatedJsonSchema['properties'] | undefined => {
  const combinatorSchemas: AnnotatedJsonSchema[] = [];
  if (Array.isArray(schema.oneOf)) {
    combinatorSchemas.push(...schema.oneOf);
  }
  if (Array.isArray(schema.anyOf)) {
    combinatorSchemas.push(...schema.anyOf);
  }
  if (Array.isArray(schema.allOf)) {
    combinatorSchemas.push(...schema.allOf);
  }

  if (combinatorSchemas.length === 0) {
    return undefined;
  }

  return combinatorSchemas.reduce<AnnotatedJsonSchema['properties']>(
    (accumulatedProperties, combinatorSchema) => ({
      ...accumulatedProperties,
      ...(combinatorSchema.properties ?? {}),
    }),
    {},
  );
};

const getSchemaPropertiesAtPath = (
  rootSchema: AnnotatedJsonSchema,
  rootSchemaData: unknown,
  schemaDataPath: string,
): AnnotatedJsonSchema['properties'] | undefined => {
  const resolvedSchemaNode = schemaDataPath
    ? getResolvedSchemaNodeAtPath(schemaDataPath, rootSchema, rootSchemaData)
    : rootSchema;

  if (!resolvedSchemaNode) {
    return undefined;
  }

  if (resolvedSchemaNode.properties) {
    return resolvedSchemaNode.properties;
  }

  // If this is a discriminator-based combinator that did not resolve to a branch,
  // skip pruning to avoid dropping branch-specific payload fields.
  const hasCombinatorBranches =
    Array.isArray(resolvedSchemaNode.oneOf) ||
    Array.isArray(resolvedSchemaNode.anyOf) ||
    Array.isArray(resolvedSchemaNode.allOf);
  if (resolvedSchemaNode.discriminator?.propertyName && hasCombinatorBranches) {
    return undefined;
  }

  return getMergedCombinatorProperties(resolvedSchemaNode);
};

const getRootEvaluationContext = (
  entity: EntityOrConfiguration,
  attributes: unknown,
): unknown => {
  if (!isConfigEntity(entity)) return attributes;
  if (!isPlainObject(attributes)) return undefined;
  return (attributes as Record<string, unknown>).domainEntityAttributes;
};

/**
 * Recursively traverses schema and data, removing fields from data
 * that match their `omitIf` conditions.
 *
 * @param schema - The schema to traverse
 * @param data - The data to filter at this level
 * @param evaluationContext - The data context used for evaluating conditions
 * @returns Filtered data with fields matching omitIf conditions removed
 */
const traverseAndRemoveMatchingFields = (
  schema: AnnotatedJsonSchema | undefined,
  data: unknown,
  evaluationContext: unknown,
  rootData: unknown,
  featureFlags: Record<string, boolean>,
  rootSchema: AnnotatedJsonSchema,
  rootSchemaData: unknown,
  schemaDataPath: string,
): unknown => {
  if (!schema || data === undefined || data === null) {
    return data;
  }

  if (schema.type === 'array' && Array.isArray(data) && schema.items) {
    // For array items, evaluate conditions against the item itself
    return data.map((item: unknown, index: number) =>
      traverseAndRemoveMatchingFields(
        schema.items as AnnotatedJsonSchema,
        item,
        item,
        rootData,
        featureFlags,
        rootSchema,
        rootSchemaData,
        schemaDataPath ? `${schemaDataPath}.${index}` : `${index}`,
      ),
    );
  }

  if ((schema.type === 'object' || schema.properties) && isObjectRecord(data)) {
    const schemaProperties = getSchemaPropertiesAtPath(
      rootSchema,
      rootSchemaData,
      schemaDataPath,
    );

    if (!schemaProperties) {
      return { ...data };
    }

    return omitBy(
      mapValues(data as Record<string, unknown>, (value, key) => {
        const propertySchema = schemaProperties[key] as
          | AnnotatedJsonSchema
          | undefined;

        // If no schema for this property, keep the value as-is
        if (!propertySchema) {
          return value;
        }

        // Recursively process nested objects/arrays
        if (value !== undefined) {
          return traverseAndRemoveMatchingFields(
            propertySchema,
            value,
            evaluationContext,
            rootData,
            featureFlags,
            rootSchema,
            rootSchemaData,
            schemaDataPath ? `${schemaDataPath}.${key}` : key,
          );
        }

        return value;
      }),
      (_value, key) => {
        const propertySchema = schemaProperties[key] as
          | AnnotatedJsonSchema
          | undefined;

        // If no schema, don't omit
        if (!propertySchema) {
          return false;
        }

        // Evaluate omitIf condition against the evaluation context
        return evaluateOmitIfCondition({
          schema: propertySchema,
          data: evaluationContext,
          options: { rootData, featureFlags },
        });
      },
    );
  }

  return data;
};

/**
 * Removes fields from entity data that match their `omitIf` conditions.
 *
 * This utility is used during entity save/update operations to ensure the API payload
 * only contains data for fields that should be included based on the current data state.
 * It prevents sending data for conditionally hidden fields that may contain stale or
 * irrelevant values.
 *
 * ## How it works
 *
 * The function traverses the entity schema looking for properties with `x-entity-conditional`
 * annotations of type `omitIf`. For each field with such a condition, it evaluates the
 * JSON Logic rule against the current data context. If the condition evaluates to `true`,
 * the field is removed from the output data.
 *
 * ## Difference from includeIf
 *
 * - `includeIf`: Only controls UI visibility. Field is shown when condition is `true`,
 *   hidden when `false`. Data is still sent to the API regardless.
 * - `omitIf`: Controls both UI visibility AND API payload filtering. Field is hidden
 *   from the UI and removed from API payloads when condition is `true`.
 *
 * Use `omitIf` when you need to prevent data from being sent to the API based on
 * conditional logic (e.g., "omit this field when feature is disabled").
 *
 * ## Nested Conditions
 *
 * The function recursively processes nested objects and arrays. For array items, each item
 * becomes its own evaluation context, allowing item-level conditional logic.
 *
 * @example
 * // Schema with omitIf condition on contentData field
 * // contentData is omitted when enrolmentData.frontLineCampaign === true
 * const filteredData = removeFieldsMatchingOmitIf(entityData, campaignEntity);
 * // If frontLineCampaign is true, contentData will be removed from the result
 *
 * @param data - The entity root data to filter
 * @param entity - The entity or entity configuration containing the schema with conditional annotations
 * @returns A new EntityRootData object with fields matching omitIf conditions removed,
 *          or the original data if no filtering is needed (undefined data, missing schema, etc.)
 */
export const removeFieldsMatchingOmitIf = (
  data: EntityRootData | undefined,
  entity: EntityOrConfiguration,
): EntityRootData | undefined => {
  if (!data) {
    return data;
  }

  if (!entity || !hasSchema(entity)) {
    return data;
  }

  const attributesProperty = entity.schema.properties?.attributes;
  if (!attributesProperty) {
    return data;
  }

  const attributesSchema = attributesProperty as AnnotatedJsonSchema;
  if (!attributesSchema.properties) {
    return data;
  }

  // - Domain entities evaluate conditions against `attributes`
  // - Config entities evaluate conditions against `attributes.domainEntityAttributes`
  const evaluationContext = getRootEvaluationContext(entity, data.attributes);

  const filteredAttributes = traverseAndRemoveMatchingFields(
    attributesSchema,
    data.attributes,
    evaluationContext,
    evaluationContext, // rootData for `$` prefixed paths
    getAllFeatureFlags(), // feature flags for `_featureFlags` prefixed paths
    attributesSchema,
    data.attributes,
    '',
  );

  return {
    ...data,
    attributes: filteredAttributes as EntityRootData['attributes'],
  };
};
